import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { getLatestBiasReview } from '../../../data/biasReviewLog';
import { getAuditLog } from '../../../services/aiAuditLog';

/**
 * AIActCompliancePanel
 * --------------------------------------------------------------------------
 * Surfaces ClearEqual's posture against the EU AI Act (Regulation 2024/1689).
 *
 * Sections:
 *   - Risk classification banner (high-risk per Annex III §4(b))
 *   - Article-by-article compliance table (Arts. 9-15)
 *   - Most-recent bias review (Art. 10)
 *   - Audit log of recent AI activity (Art. 12)
 *
 * Designed to be droppable into the compliance-reports page or any other
 * admin surface. Reads live data from biasReviewLog and aiAuditLog services.
 */

const AIActCompliancePanel = () => {
  const { t } = useTranslation();
  const [auditEntries, setAuditEntries] = useState([]);

  useEffect(() => {
    setAuditEntries(getAuditLog({ limit: 10 }));
  }, []);

  const articles = [
    { num: '9',  status: 'compliant', titleKey: 'aiAct.art9Title',  reqKey: 'aiAct.art9Req',  evKey: 'aiAct.art9Ev'  },
    { num: '10', status: 'compliant', titleKey: 'aiAct.art10Title', reqKey: 'aiAct.art10Req', evKey: 'aiAct.art10Ev' },
    { num: '11', status: 'in_progress', titleKey: 'aiAct.art11Title', reqKey: 'aiAct.art11Req', evKey: 'aiAct.art11Ev' },
    { num: '12', status: 'compliant', titleKey: 'aiAct.art12Title', reqKey: 'aiAct.art12Req', evKey: 'aiAct.art12Ev' },
    { num: '13', status: 'compliant', titleKey: 'aiAct.art13Title', reqKey: 'aiAct.art13Req', evKey: 'aiAct.art13Ev' },
    { num: '14', status: 'compliant', titleKey: 'aiAct.art14Title', reqKey: 'aiAct.art14Req', evKey: 'aiAct.art14Ev' },
    { num: '15', status: 'in_progress', titleKey: 'aiAct.art15Title', reqKey: 'aiAct.art15Req', evKey: 'aiAct.art15Ev' },
  ];

  const StatusBadge = ({ status }) => {
    if (status === 'compliant') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success border border-success/20">
          <Icon name="CheckCircle2" size={12} />
          {t('aiAct.statusCompliant')}
        </span>
      );
    }
    if (status === 'in_progress') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-warning/10 text-warning border border-warning/30">
          <Icon name="Clock" size={12} />
          {t('aiAct.statusInProgress')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground border border-border">
        {t('aiAct.statusNotApplicable')}
      </span>
    );
  };

  const biasReview = getLatestBiasReview();

  const opLabel = (op) => {
    if (op === 'cv_parse') return t('aiAct.auditOpCvParse');
    if (op === 'skill_match') return t('aiAct.auditOpSkillMatch');
    if (op === 'ai_insight') return t('aiAct.auditOpAiInsight');
    return op;
  };

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header + classification */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Shield" size={20} className="text-primary" />
              <h2 className="text-lg font-bold text-foreground">{t('aiAct.title')}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{t('aiAct.subtitle')}</p>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{t('aiAct.effective')}</span>
        </div>

        <div className="flex items-center gap-3 p-3 bg-warning/5 border border-warning/30 rounded-lg">
          <Icon name="AlertTriangle" size={18} className="text-warning shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">{t('aiAct.classification')}</p>
            <p className="text-sm text-foreground">
              <span className="font-bold text-warning">{t('aiAct.classificationValue')}</span>
              <span className="text-muted-foreground">, {t('aiAct.classificationBasis')}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Article compliance table */}
      <div className="bg-card border border-border rounded-lg shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{t('aiAct.articleHeader')}</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{t('aiAct.requirementHeader')}</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{t('aiAct.statusHeader')}</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{t('aiAct.evidenceHeader')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {articles.map((a) => (
                <tr key={a.num} className="hover:bg-muted/20">
                  <td className="px-4 py-3 align-top">
                    <p className="font-semibold text-sm text-foreground whitespace-nowrap">{t(a.titleKey)}</p>
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-foreground">{t(a.reqKey)}</td>
                  <td className="px-4 py-3 align-top"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3 align-top text-sm text-muted-foreground">{t(a.evKey)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bias review */}
      {biasReview && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="UserCheck" size={18} className="text-primary" />
            <h3 className="font-semibold text-foreground">{t('aiAct.biasReviewTitle')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{t('aiAct.biasReviewer')}</p>
              <p className="text-sm font-medium text-foreground">{biasReview.reviewerName}</p>
              <p className="text-xs text-muted-foreground italic">{t(biasReview.reviewerRoleKey)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('aiAct.biasNextDue')}</p>
              <p className="text-sm font-medium text-foreground">{biasReview.nextReviewDue}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-muted-foreground mb-1">{t('aiAct.biasScope')}</p>
              <p className="text-sm text-foreground">{t(biasReview.scopeKey)}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-muted-foreground mb-1">{t('aiAct.biasFindings')}</p>
              <ul className="text-sm text-foreground list-disc pl-5 space-y-0.5">
                {biasReview.findingKeys.map((k, i) => <li key={i}>{t(k)}</li>)}
              </ul>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-muted-foreground mb-1">{t('aiAct.biasActions')}</p>
              <p className="text-sm text-foreground">{t(biasReview.actionsKey)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Audit log */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="ListChecks" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">{t('aiAct.auditLogTitle')}</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t('aiAct.auditLogBody')}</p>
        {auditEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4 text-center">{t('aiAct.auditNoEntries')}</p>
        ) : (
          <div className="space-y-2">
            {auditEntries.map((entry, i) => (
              <div key={i} className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded text-sm">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <Icon
                    name={entry.operation === 'cv_parse' ? 'FileText' : entry.operation === 'skill_match' ? 'Target' : 'Cpu'}
                    size={14}
                    className="text-muted-foreground shrink-0 mt-0.5"
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{opLabel(entry.operation)}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {entry.operation === 'cv_parse' && entry.filename}
                      {entry.operation === 'skill_match' && `${entry.roleId}, ${entry.scorePercent}%`}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(entry.timestamp)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIActCompliancePanel;
